import isEmail from 'validator/lib/isEmail';
import isURL from 'validator/lib/isURL';

type ValidationRule = Record<string, unknown>;

export const PREVIOUSLY_ENTERED = 'Previously entered';

export const US_TAX_ID_VALIDATION = {
  pattern: /^\d{2}-?\d{7}$|^\d{3}-?\d{2}-?\d{4}$/,
  formatMessage: 'Format must be 00-0000000, 000-00-0000, or 000000000.',
};

export const CANADA_SIN_VALIDATION = {
  pattern: /^\d{3}[\s-]?\d{3}[\s-]?\d{3}$/,
  formatMessage: 'Format must be 000-000-000, 000 000 000, or 000000000.',
};

export const US_ZIP_CODE_VALIDATION = {
  pattern: /^\s*\d{5}\s*$/,
  formatMessage: 'Postal Code must be only numbers (up to 5 digits).',
};

export const CANADA_POSTAL_CODE_VALIDATION = {
  pattern: /^[A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d$/,
  formatMessage: 'Postal Code must be in the format A1A 1A1, A1A-1A1, or A1A1A1.',
};

export const ACCOUNT_NUMBER_PATTERN = /^(\d{3,200})$/;

export const trimString: ValidationRule = {
  setValueAs: (value: unknown): string => (typeof value === 'string' ? value.trim() : (value as string)),
};

export const requiredText = (label: string): ValidationRule => ({
  required: `${label} is required.`,
});

export const maxLengthRule = (max: number, label?: string): ValidationRule => ({
  maxLength: {
    value: max,
    message: label ? `${label} must not be more than ${max} characters.` : `Maximum ${max} characters allowed.`,
  },
});

export const patternRule = (pattern: RegExp, message: string): ValidationRule => ({
  pattern: {
    value: pattern,
    message,
  },
});

export const emailPatternRule = (label = 'Email Address'): ValidationRule => ({
  validate: {
    isValidEmail: (value: string | null | undefined) => (value && isEmail(value)) || `${label} is invalid.`,
  },
});

export const websiteUrlRule = (label = 'Website URL'): ValidationRule => ({
  validate: {
    isValidUrl: (value: string | null | undefined) => {
      if (value === null || value === undefined) {
        return true;
      }

      if (typeof value !== 'string' || value.trim().length === 0) {
        return true;
      }

      return isURL(value) || `${label} is invalid.`;
    },
  },
});

export const taxIdPatternRule = (label: string, isCanada: boolean): ValidationRule => {
  const validation = isCanada ? CANADA_SIN_VALIDATION : US_TAX_ID_VALIDATION;
  return patternRule(validation.pattern, `${label} is invalid. ${validation.formatMessage}`);
};

export const postalCodePatternRule = (isCanada: boolean): ValidationRule => {
  const validation = isCanada ? CANADA_POSTAL_CODE_VALIDATION : US_ZIP_CODE_VALIDATION;
  return patternRule(validation.pattern, validation.formatMessage);
};

export const ROUTING_NUMBER_VALIDATION = {
  ...requiredText('Routing Number'),
  ...patternRule(/^(\d{9})$/, 'Routing Number is invalid.'),
  ...trimString,
};

export const validateAccountNumber = (value: string | null) => {
  if (value === PREVIOUSLY_ENTERED) return true;
  if (!value || value.trim() === '') return 'Account Number is required.';
  if (!ACCOUNT_NUMBER_PATTERN.test(value)) return 'Account Number is invalid.';
  return true;
};

export const confirmAccountNumberRule = (accountNumber: string | null): ValidationRule => ({
  ...requiredText('Account Number'),
  validate: (value: string | null | undefined) => value === accountNumber || 'Account numbers do not match',
  ...trimString,
});

export const INSTITUTION_NUMBER_VALIDATION = {
  required: 'Institution Number is required.',
  ...patternRule(/^\d{3}$/, 'Institution Number must be exactly 3 digits.'),
  ...trimString,
};

export const TRANSIT_NUMBER_VALIDATION = {
  required: 'Transit Number is required.',
  ...patternRule(/^\d{5}$/, 'Transit Number must be exactly 5 digits.'),
  ...trimString,
};

export const validateCreditCardNumber = (value: string | null) => {
  if (value === PREVIOUSLY_ENTERED) return true;
  if (!value || value.trim() === '') return 'Credit Card Number is required.';
  if (!/^\d{13,16}$/.test(value.trim())) return 'Credit Card Number must be 13-16 digits.';
  return true;
};
